<template>
  <div class="personnel-management">
    <!-- Navigation Bar -->
    <div v-if="user.userauthority_govem != 1" class="nav-wrapper">
      <personnel_bar />
    </div>

    <v-container fluid class="main-container">
      <!-- Header Card -->
      <div class="header-card">
        <div class="header-content">
          <div class="header-icon">
            <v-icon size="32" color="primary">mdi-clipboard-text</v-icon>
          </div>
          <div class="header-text">
            <h2 class="header-title">ข้าราชการครูและบุคลากรทางการศึกษา</h2>
            <p class="header-subtitle">จัดการข้อมูลบุคลากรในระบบ</p>
          </div>
        </div>
      </div>

      <!-- =============================================== -->
      <!-- Dashboard: สถิติภาพรวม (ApexCharts)               -->
      <!-- =============================================== -->
      <v-row class="content-row">
        <v-col cols="12" md="6">
          <div class="card chart-card">
            <div class="card-header">
              <h3 class="card-title">สัดส่วนบุคลากรตามประเภทตำแหน่ง</h3>
              <v-chip small color="primary" outlined
                >{{ totalPersonnelCount }} คน</v-chip
              >
            </div>
            <div class="chart-body">
              <apexchart
                v-if="donutChartSeries.length"
                type="donut"
                height="300"
                :options="donutChartOptions"
                :series="donutChartSeries"
              ></apexchart>
              <div v-else class="chart-empty">
                <v-icon color="grey lighten-1" size="40"
                  >mdi-chart-donut</v-icon
                >
                <p class="grey--text mt-2">ไม่มีข้อมูลสำหรับแสดงผล</p>
              </div>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <div class="card chart-card">
            <div class="card-header">
              <h3 class="card-title">จำนวนบุคลากรตามวิทยฐานะ</h3>
            </div>
            <div class="chart-body">
              <apexchart
                v-if="rangBarSeries[0].data.length"
                type="bar"
                height="300"
                :options="rangBarOptions"
                :series="rangBarSeries"
              ></apexchart>
              <div v-else class="chart-empty">
                <v-icon color="grey lighten-1" size="40">mdi-chart-bar</v-icon>
                <p class="grey--text mt-2">ไม่มีข้อมูลสำหรับแสดงผล</p>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Main Content Grid -->
      <v-row class="content-row">
        <!-- Statistics Table -->
        <v-col cols="12" md="8">
          <div class="card stats-card">
            <div class="card-header">
              <h3 class="card-title">สถิติตามประเภท</h3>
              <v-text-field
                v-model="search_postion"
                placeholder="ค้นหา..."
                dense
                outlined
                hide-details
                prepend-inner-icon="mdi-magnify"
                class="search-field-compact"
              ></v-text-field>

              <v-btn
                small
                color="success"
                dark
                :loading="exportingExcel"
                @click="exportPersonnelStatsToExcel()"
              >
                <v-icon small left>mdi-file-excel</v-icon>
                Export Excel
              </v-btn>
            </div>

            <v-data-table
              :items="personnel_sCountsWithPercent"
              :headers="personnel_sHeaders"
              :search="search_postion"
              class="clean-table"
              hide-default-footer
              :items-per-page="10"
            >
              <template v-slot:item.count_grouptype="{ item }">
                <v-btn
                  text
                  small
                  color="primary"
                  class="count-btn"
                  :loading="exportingType === item.position_name"
                  @click="exportPersonnelByTypeToExcel(item)"
                >
                  {{ item.count_grouptype }}
                  <v-icon small right>mdi-file-excel</v-icon>
                </v-btn>
              </template>

              <!-- ⭐ ใหม่: แสดง % พร้อม progress bar -->
              <template v-slot:item.percent="{ item }">
                <div
                  class="d-flex align-center justify-center"
                  style="gap:8px;"
                >
                  <span style="min-width:44px;">{{ item.percent }}%</span>
                  <v-progress-linear
                    :value="item.percent"
                    height="8"
                    rounded
                    color="primary"
                    style="max-width:120px;"
                  ></v-progress-linear>
                </div>
              </template>

              <!-- ⭐ ใหม่: แถวรวมท้ายตาราง -->
              <template v-slot:body.append>
                <tr style="background:#fafafa; font-weight:600;">
                  <td class="text-center">รวมทั้งหมด</td>
                  <td class="text-center">{{ totalPersonnelCount }}</td>
                  <td class="text-center">100%</td>
                </tr>
              </template>
            </v-data-table>
          </div>
        </v-col>

        <!-- Action Panel -->
        <v-col cols="12" md="4">
          <!-- Info Card for Admin -->
          <div v-if="user.userauthority_govem === 1" class="card info-card">
            <div class="info-icon-wrapper">
              <v-icon size="48" color="primary">mdi-chart-bar</v-icon>
            </div>
            <h3 class="info-title">สถิติข้อมูลบุคลากร</h3>
            <p class="info-description">
              แสดงข้อมูลภาพรวมจำนวนข้าราชการครูและบุคลากรทางการศึกษาในระบบ
            </p>
          </div>

          <!-- Action Buttons for Non-Admin -->
          <div v-else class="action-panel">
            <v-btn
              block
              large
              elevation="0"
              color="primary"
              class="action-btn mb-3"
              @click.native="personnel_temporaryAdd()"
            >
              <v-icon left>mdi-plus-circle-outline</v-icon>
              เพิ่มรายการ
            </v-btn>

            <v-btn
              block
              large
              elevation="0"
              outlined
              color="primary"
              class="action-btn mb-3"
              to="/admin/personnel_import_reconcile"
            >
              <v-icon left>mdi-file-excel</v-icon>
              Import Excel
            </v-btn>

            <v-btn
              block
              large
              elevation="0"
              outlined
              color="primary"
              class="action-btn mb-3"
              @click.native="personnel_temporaryCSV()"
            >
              <v-icon left>mdi-file-upload</v-icon>
              นำเข้าไฟล์ CSV (เพิ่มและปรับปรุง)
            </v-btn>

            <v-btn
              block
              large
              elevation="0"
              outlined
              color="primary"
              class="action-btn mb-3"
              @click.native="personnel_temporaryAssTeachCSV()"
            >
              <v-icon left>mdi-account-convert</v-icon>
              นำเข้า CSV (ครูผู้ช่วย → ครู)
            </v-btn>

            <v-btn
              block
              large
              elevation="0"
              outlined
              color="primary"
              class="action-btn mb-3"
              @click.native="personnel_temporaryOutofCSV()"
            >
              <v-icon left>mdi-account-remove</v-icon>
              นำเข้าผู้ออกจากระบบ
            </v-btn>

            <v-divider class="my-4"></v-divider>

            <v-btn
              block
              large
              elevation="0"
              outlined
              class="action-btn mb-3"
              @click="UpdateJ18()"
            >
              <v-icon left>mdi-update</v-icon>
              ปรับปรุงข้อมูล จ.18
            </v-btn>

            <v-btn
              block
              large
              elevation="0"
              outlined
              class="action-btn mb-3"
              @click="clearSearch()"
            >
              <v-icon left>mdi-filter-remove</v-icon>
              Clear Search
            </v-btn>

            <v-btn
              block
              large
              elevation="0"
              outlined
              color="error"
              class="action-btn mb-3"
              @click="clearRetireSearch()"
            >
              <v-icon left>mdi-account-clock</v-icon>
              ปรับปรุงข้อมูลผู้เกษียณ
            </v-btn>

            <v-btn
              block
              large
              elevation="0"
              outlined
              class="action-btn"
              @click="repleteSearch()"
            >
              <v-icon left>mdi-content-duplicate</v-icon>
              เลขที่ตำแหน่งซ้ำ
            </v-btn>
          </div>
        </v-col>

        <!-- Search and Filter Section -->
        <v-col cols="12" :md="user.userauthority_govem === 1 ? 12 : 8">
          <div class="card search-card">
            <div class="search-header">
              <h3 class="card-title">ค้นหาและกรองข้อมูล</h3>
              <span class="search-hint">
                พิมพ์เลขบัตรประชาชน (13 หลัก) / เลขที่ตำแหน่ง / ปีเกษียณ (พ.ศ.)
                หรือ ชื่อ-สกุล ระบบจะตรวจจับรูปแบบให้อัตโนมัติ
              </span>
            </div>

            <!-- Smart Search -->
            <v-text-field
              v-model="search"
              placeholder="พิมพ์คำค้นหา เช่น ชื่อ, นามสกุล, รหัสบัตรประชาชน 13 หลัก, เลขที่ตำแหน่ง, ปีเกษียณ..."
              outlined
              dense
              hide-details="auto"
              prepend-inner-icon="mdi-magnify"
              clearable
              v-on:keyup.enter="OnEnter()"
              @click:clear="clearSearch()"
              class="search-field mb-2"
            >
              <template v-slot:append>
                <v-chip
                  v-if="detectedSearchType"
                  x-small
                  color="primary"
                  text-color="white"
                >
                  {{ detectedSearchType }}
                </v-chip>
              </template>
            </v-text-field>

            <div class="text-right mb-4">
              <v-btn small color="primary" elevation="0" @click="OnEnter()">
                <v-icon small left>mdi-magnify</v-icon>ค้นหา
              </v-btn>
              <v-btn
                small
                outlined
                class="ml-2"
                @click="personnel_temporaryQueryAll()"
              >
                <v-icon small left>mdi-format-list-bulleted</v-icon>แสดงทั้งหมด
              </v-btn>
            </div>

            <!-- Retirement Year Action -->
            <div
              v-if="search >= 2000 && user.userauthority_govem != 1"
              class="mb-4"
            >
              <v-btn
                elevation="0"
                outlined
                :href="
                  '#/admin/print_personnel_retrire_year/?retrire_year=' + search
                "
                target="_blank"
              >
                <v-icon left small>mdi-printer</v-icon>
                พิมพ์ผู้เกษียณปี {{ search }}
              </v-btn>
            </div>

            <!-- Filter Row -->
            <v-row dense>
              <v-col cols="12" sm="6" md="2">
                <v-select
                  v-model="rangname_search"
                  :items="rang_names"
                  item-text="text"
                  item-value="value"
                  label="วิทยฐานะ"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="search_rang()"
                  class="filter-select"
                ></v-select>
              </v-col>

              <v-col cols="12" sm="6" md="2">
                <v-select
                  v-model="position_search"
                  :items="position_select"
                  item-text="text"
                  item-value="value"
                  label="ตำแหน่ง"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="search_position()"
                  class="filter-select"
                ></v-select>
              </v-col>

              <v-col cols="12" sm="6" md="2">
                <v-autocomplete
                  v-model="college_search"
                  :items="users"
                  item-text="agencys"
                  item-value="user_code"
                  label="สถานศึกษา"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="search_college()"
                  class="filter-select"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" sm="6" md="2">
                <v-autocomplete
                  v-model="user_status_search"
                  :items="userstatus"
                  item-text="user_status_name"
                  item-value="user_status_sub"
                  label="ประเภทบุคคล"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="search_user_status()"
                  class="filter-select"
                ></v-autocomplete>
              </v-col>

              <!-- สาขาวิชาบรรจุ -->
              <v-col cols="12" sm="6" md="2">
                <v-autocomplete
                  v-model="branch_search"
                  :items="branch"
                  item-text="name_branch"
                  item-value="id_branch"
                  label="สาขาวิชาบรรจุ"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="search_branch()"
                  class="filter-select"
                >
                  <template v-slot:item="{ item }">
                    <div>{{ item.id_branch }} : {{ item.name_branch }}</div>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- สาขาวิชาที่สอน/เกี่ยวข้อง/ถนัด -->
              <v-col cols="12" sm="6" md="2">
                <v-autocomplete
                  v-model="course_branch_search"
                  :items="rate_work_course_stds"
                  item-text="branch_name_th"
                  item-value="id_course_branch"
                  label="สาขาวิชาที่สอน/ถนัด"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="search_course_branch()"
                  class="filter-select"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </div>
        </v-col>

        <!-- Duplicate Positions List -->
        <v-col cols="12" md="4" v-if="user.userauthority_govem != 1">
          <div class="card duplicate-card">
            <div class="card-header">
              <h3 class="card-title">ตำแหน่งซ้ำ</h3>
            </div>
            <v-simple-table fixed-header height="250px" class="clean-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center">เลขที่ตำแหน่ง</th>
                    <th class="text-center">จำนวน</th>
                    <th class="text-center">ตำแหน่ง</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in personnel_temporary_idpositions"
                    :key="item.id"
                  >
                    <td class="text-center">
                      <v-btn
                        text
                        small
                        color="primary"
                        @click="search_idpositionClick(item.id_position)"
                      >
                        {{ item.id_position }}
                      </v-btn>
                    </td>
                    <td class="text-center">
                      <v-chip small>{{ item.countIDposition }}</v-chip>
                    </td>
                    <td class="text-center text-caption">
                      {{ item.position_name }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </v-col>
      </v-row>

      <!-- Main Data Table -->
      <div class="card data-table-card">
        <v-data-table
          :headers="filteredHeaders"
          :items="personnel_temporarys"
          :loading="loading"
          :search="search"
          class="clean-table elevation-0"
          loading-text="กำลังโหลดข้อมูล..."
        >
          <!-- Custom slots remain the same, just with updated button styles -->
          <template v-slot:item.viewId="{ item }">
            <v-btn
              :href="'#/user/resume/?id_card=' + item.id_card"
              target="_blank"
              icon
              small
              color="primary"
            >
              <v-icon small>mdi-printer</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.govem_contract="{ item }">
            <v-btn
              v-if="
                String(item.govem_contract).length > 10 &&
                  item.user_status === 'gov_em'
              "
              @click="govem_contract_showDialog(item.govem_contract)"
              icon
              small
              color="primary"
            >
              <v-icon small>mdi-file-document</v-icon>
            </v-btn>
            <v-icon
              v-else-if="item.user_status === 'gov_em'"
              color="error"
              small
            >
              mdi-alert-circle
            </v-icon>
          </template>

          <template v-slot:item.reset="{ item }">
            <v-btn
              fab
              small
              dark
              color="warning"
              @click="personnel_etcReset(item.id_card)"
              ><v-icon>mdi-reload</v-icon></v-btn
            >
          </template>

          <template v-slot:item.brith_day="{ item }">
            {{ item.brith_day }}/{{ item.brith_month }}/{{ item.brith_year }}
          </template>

          <template v-slot:item.appoin_day="{ item }">
            {{ item.appoin_day }}/{{ item.appoin_month }}/{{ item.appoin_year }}
          </template>

          <template v-slot:item.date_app_now="{ item }">
            {{
              item.date_app_now
                | moment("add", "543 years")
                | moment("D MMMM YYYY")
            }}
          </template>

          <template v-slot:item.date_app_positonnow="{ item }">
            {{
              item.date_app_positonnow
                | moment("add", "543 years")
                | moment("D MMMM YYYY")
            }}
          </template>

          <!-- สาขาวิชาบรรจุ -->
          <template v-slot:item.pt_id_branch="{ item }">
            {{ getBranchName(item.pt_id_branch) }}
          </template>

          <!-- สาขาวิชาที่สอน/เกี่ยวข้อง/ถนัด -->
          <template v-slot:item.personnel_tem_branch="{ item }">
            {{ getCourseBranchName(item.personnel_tem_branch) }}
          </template>

          <template v-slot:item.Login="{ item }">
            <v-btn
              icon
              small
              color="primary"
              @click="
                userLogin(
                  item.id_card,
                  `${item.title_s || ''}${item.frist_name || ''} ${item.last_name || ''}`
                )
              "
            >
              <v-icon small>mdi-login</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.actions="{ item }">
            <div
              v-if="
                user.userauthority_govem === 1 ||
                  user.userauthority_govem === '1'
              "
            >
              <v-btn
                v-if="item.user_status === 'gov_em'"
                icon
                small
                color="warning"
                @click.stop="personnel_temporaryEdit(item.id_card)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-icon v-else small color="grey lighten-1">mdi-minus</v-icon>
            </div>
            <div
              v-if="
                user.userauthorityAdmin === 1 || user.userauthorityAdmin === '1'
              "
            >
              <v-btn
                icon
                small
                color="warning"
                @click.stop="personnel_temporaryEdit(item.id_card)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>
          </template>

          <template v-slot:item.dels="{ item }">
            <div
              v-if="
                user.userauthority_govem === 1 ||
                  user.userauthority_govem === '1'
              "
            >
              <v-btn
                v-if="item.user_status === 'gov_em'"
                icon
                small
                color="error"
                @click.stop="personnel_temporaryDelete(item.id_card)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
              <v-icon v-else small color="grey lighten-1">mdi-minus</v-icon>
            </div>
            <div
              v-if="
                user.userauthorityAdmin === 1 || user.userauthorityAdmin === '1'
              "
            >
              <v-btn
                icon
                small
                color="error"
                @click.stop="personnel_temporaryDelete(item.id_card)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>

          <template v-slot:no-results>
            <div class="no-results">
              <v-icon large color="grey">mdi-magnify</v-icon>
              <p class="mt-2">ไม่พบผลลัพธ์สำหรับ "{{ search }}"</p>
            </div>
          </template>
        </v-data-table>
      </div>

      <!--addpersonnel_temporarydialog  -->
      <v-layout row justify-center>
        <v-dialog
          v-model="addpersonnel_temporarydialog"
          persistent
          max-width="80%"
        >
          <v-card class="mx-auto pa-5">
            <base-material-card
              icon="mdi-account-multiple"
              title="เพิ่มข้อมูลข้าราชการครูและบุคลากรทางการศึกษา"
              class="px-5 py-3 text_google"
            >
            </base-material-card>

            <v-card-text>
              <v-form ref="addpersonnel_temporaryform" lazy-validation>
                <v-container grid-list-md>
                  <v-row no-gutters>
                    <v-col cols="12" md="12">
                      <v-radio-group v-model="radiosTpyeOganize" row>
                        <template v-slot:label>
                          <div>
                            สังกัด
                            <strong
                              >วิทยาลัย / สถาบัน / หน่วยศึกษานิเทศก์ /
                              ส่วนกลาง</strong
                            >
                          </div>
                        </template>
                        <v-radio value="college">
                          <template v-slot:label>
                            <div>
                              <strong class="success--text">วิทยาลัย</strong>
                            </div>
                          </template>
                        </v-radio>
                        <v-radio value="iven">
                          <template v-slot:label>
                            <div>
                              <strong class="primary--text">สถาบัน</strong>
                            </div>
                          </template>
                        </v-radio>
                        <v-radio value="supervision">
                          <template v-slot:label>
                            <div>
                              <strong class="primary--text"
                                >หน่วยศึกษานิเทศก์</strong
                              >
                            </div>
                          </template>
                        </v-radio>
                        <v-radio value="ovec">
                          <template v-slot:label>
                            <div>
                              <strong class="primary--text">ส่วนกลาง</strong>
                            </div>
                          </template>
                        </v-radio>
                      </v-radio-group>
                    </v-col>
                    <v-col cols="12" md="12">
                      <hr style="border: 1px solid grey;" />
                    </v-col>

                    <v-col cols="12" md="12">
                      <span class="font-weight-bold"
                        >1. ประเภทบุคคล/สังกัด</span
                      >
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        rounded
                        filled
                        v-model="addpersonnel_temporary.user_status"
                        :items="userstatus"
                        item-text="user_status_name"
                        item-value="user_status_sub"
                        label="Type"
                        required
                        :rules="[v => !!v || '']"
                        prepend-icon="mdi-format-list-bulleted-type"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.college_code"
                        :items="users"
                        item-text="agencys"
                        item-value="user_code"
                        label="วิทยาลัย"
                        @change="man_powerQuery()"
                        required
                        :rules="[v => !!v || '']"
                        prepend-icon="mdi-school"
                      >
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="12">
                      <hr style="border: 1px solid grey;" />
                    </v-col>
                    <v-col cols="12" md="12"
                      ><span class="font-weight-bold"
                        >2. ข้อมูลพื้นฐาน</span
                      ></v-col
                    >

                    <v-col cols="12" md="3">
                      <v-text-field
                        @input="validateInputAdd()"
                        prepend-icon="mdi-account-key"
                        rounded
                        outlined
                        label="รหัสบัตรประชาชน"
                        v-model="addpersonnel_temporary.id_card"
                        :rules="[
                          v => (!!v && v.length == 13) || '13  ID Card',
                          validateInputcheck === 'true' ||
                            'กำหนดรหัสบัตรประชาชนไม่ถูกต้อง'
                        ]"
                        maxlength="13"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-autocomplete
                        :items="prefixs"
                        item-text="prefix_name"
                        item-value="prefix_name"
                        prepend-icon="mdi-account-box"
                        rounded
                        outlined
                        label="คำนำหน้าชื่อ"
                        v-model="addpersonnel_temporary.title_s"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        prepend-icon="mdi-account-box"
                        rounded
                        outlined
                        label="ชื่อ"
                        v-model="addpersonnel_temporary.frist_name"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        prepend-icon="mdi-account-box"
                        rounded
                        outlined
                        label="นามสกุล"
                        v-model="addpersonnel_temporary.last_name"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-select
                        :items="days"
                        item-text="text"
                        item-value="value"
                        prepend-icon="mdi-calendar"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.brith_day"
                        label="วันที่เกิด : 8"
                      ></v-select>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-select
                        :items="mounts"
                        item-text="text"
                        item-value="value"
                        prepend-icon="mdi-calendar"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.brith_month"
                        label="เดือนเกิด : 8"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        :items="years"
                        item-text="text"
                        item-value="value"
                        prepend-icon="mdi-calendar"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.brith_year"
                        label="ปีเกิด : 2531"
                        type="number"
                        maxlength="4"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        prepend-icon="mdi-cellphone"
                        rounded
                        outlined
                        label="เบอร์โทร"
                        v-model="addpersonnel_temporary.tel_p"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        prepend-icon="mdi-email"
                        rounded
                        outlined
                        label="E-mail"
                        v-model="addpersonnel_temporary.e_mail"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="12">
                      <hr style="border: 1px solid grey;" />
                    </v-col>
                    <v-col cols="12" md="12"
                      ><span class="font-weight-bold">3. ตำแหน่ง</span></v-col
                    >

                    <v-col cols="12" md="4">
                      <v-autocomplete
                        prepend-icon="mdi-account-settings"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.position_name"
                        :items="position_select"
                        item-text="text"
                        item-value="text"
                        label="ตำแหน่ง"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-autocomplete
                        prepend-icon="mdi-account-settings"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.user_status"
                        :items="userstatus"
                        item-text="user_status_name"
                        item-value="user_status_sub"
                        label="สถานะ"
                      ></v-autocomplete>
                    </v-col>
                    <v-col
                      cols="12"
                      md="4"
                      v-if="radiosTpyeOganize === 'college'"
                    >
                      <v-autocomplete
                        prepend-icon="mdi-account-settings"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.id_position"
                        :items="man_powers"
                        item-text="id_position"
                        item-value="id_position"
                        label="เลขที่ตำแหน่ง"
                        ><template v-slot:item="{ item }">
                          <div>
                            <div class="font-weight-bold">
                              {{ item.id_position }}
                            </div>
                            <div>{{ item.positions }} : {{ item.id_card }}</div>
                          </div>
                        </template></v-autocomplete
                      >
                    </v-col>
                    <v-col
                      cols="12"
                      md="4"
                      v-if="
                        radiosTpyeOganize === 'iven' ||
                          radiosTpyeOganize === 'supervision' ||
                          radiosTpyeOganize === 'ovec'
                      "
                    >
                      <v-text-field
                        prepend-icon="mdi-account-settings"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.id_position"
                        label="เลขที่ตำแหน่ง"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        prepend-icon="mdi-account-settings"
                        outlined
                        rounded
                        :items="rang_names"
                        item-text="text"
                        item-value="value"
                        label="วิทยฐานะ"
                        v-model="addpersonnel_temporary.rang_name"
                        required
                        :rules="[v => !!v || '']"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-select
                        prepend-icon="mdi-account-settings"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.rang_level"
                        :items="rang_levels"
                        item-text="text"
                        item-value="value"
                        label="ระดับ"
                      ></v-select>
                    </v-col>

                    <!-- สาขาวิชาบรรจุ -->
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        prepend-icon="mdi-bookshelf"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.pt_id_branch"
                        :items="branch"
                        item-text="name_branch"
                        item-value="id_branch"
                        label="สาขาวิชาบรรจุ"
                      >
                        <template v-slot:item="{ item }">
                          <div>
                            {{ item.id_branch }} : {{ item.name_branch }}
                          </div>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <!-- สาขาวิชาที่สอน/เกี่ยวข้อง/ถนัด -->
                    <v-col
                      cols="12"
                      md="6"
                      v-if="
                        addpersonnel_temporary.user_status === 'teacher' ||
                          addpersonnel_temporary.user_status === 'gov_em'
                      "
                    >
                      <v-autocomplete
                        prepend-icon="mdi-teach"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.personnel_tem_branch"
                        :items="rate_work_course_stds"
                        item-text="branch_name_th"
                        item-value="id_course_branch"
                        label="สาขาวิชาที่สอน/เกี่ยวข้อง/ถนัด"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="12">
                      <hr style="border: 1px solid grey;" />
                    </v-col>
                    <v-col cols="12" md="12"
                      ><span class="font-weight-bold"
                        >4. วุฒิการศึกษา</span
                      ></v-col
                    >

                    <v-col cols="12" md="6">
                      <v-text-field
                        prepend-icon="mdi-account-settings"
                        rounded
                        outlined
                        label="คุณวิฒิ"
                        v-model="addpersonnel_temporary.ed_abb"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        prepend-icon="mdi-account-settings"
                        rounded
                        outlined
                        label="สาขาวิชา"
                        v-model="addpersonnel_temporary.ed_name"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="12">
                      <hr style="border: 1px solid grey;" />
                    </v-col>
                    <v-col cols="12" md="12"
                      ><span class="font-weight-bold">5. การบรรจุ</span></v-col
                    >

                    <v-col cols="12" md="4">
                      <v-autocomplete
                        prepend-icon="mdi-account-settings"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.type_personnel"
                        :items="type_personnelApp"
                        item-text="text"
                        item-value="value"
                        label="ประเภทการบรรจุ"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="4" class="pa-5">
                      <v-checkbox
                        v-model="addpersonnel_temporary.rang_condition"
                        value="1"
                        hide-details
                        class="shrink mr-2 mt-0"
                        label="กลุ่ม จชต. ชายแดนภาคใต้"
                      ></v-checkbox>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        prepend-icon="mdi-content-duplicate"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.order_app_now"
                        label="คำสั่งที่บรรจุ"
                      ></v-text-field>
                    </v-col>

                    <!-- บรรจะ -->
                    <v-col cols="12" md="4">
                      <v-select
                        :items="days"
                        item-text="text"
                        item-value="value"
                        prepend-icon="mdi-calendar-clock"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.appoin_day"
                        label="วันที่บรรจุ"
                      ></v-select>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-select
                        :items="mounts"
                        item-text="text"
                        item-value="value"
                        prepend-icon="mdi-calendar-clock"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.appoin_month"
                        label="เดือนบรรจุ"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        :items="years"
                        item-text="text"
                        item-value="value"
                        prepend-icon="mdi-calendar-clock"
                        rounded
                        outlined
                        v-model="addpersonnel_temporary.appoin_year"
                        label="ปีบรรจุ"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="12">
                      <hr style="border: 1px solid grey;" />
                    </v-col>
                    <v-col cols="12" md="12"
                      ><span class="font-weight-bold"
                        >6. การปฏิบัติงาน ณ ปัจจุบัน</span
                      ></v-col
                    >

                    <v-col cols="12" md="6">
                      <v-menu
                        ref="menu5"
                        v-model="menu5"
                        :close-on-content-click="false"
                        :return-value.sync="date"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            prepend-icon="mdi-calendar-clock"
                            rounded
                            outlined
                            v-model="addpersonnel_temporary.date_app_now"
                            label="วันเดือนปีที่เริ่มปฏิบัติงาน ณ สถานศึกษาปัจจุบัน"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            locale="th"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="addpersonnel_temporary.date_app_now"
                          no-title
                          scrollable
                          locale="th"
                        >
                          <v-spacer></v-spacer>
                          <v-btn text color="primary" @click="menu5 = false">
                            Cancel
                          </v-btn>
                          <v-btn
                            text
                            color="primary"
                            @click="$refs.menu5.save(date)"
                          >
                            OK
                          </v-btn>
                        </v-date-picker>
                      </v-menu>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-menu
                        ref="menu6"
                        v-model="menu6"
                        :close-on-content-click="false"
                        :return-value.sync="date"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            prepend-icon="mdi-calendar-clock"
                            rounded
                            outlined
                            v-model="addpersonnel_temporary.date_app_positonnow"
                            label="วันเดือนปีที่เริ่มปฏิบัติงาน ใน ตำแหน่งปัจจุบัน"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            locale="th"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="addpersonnel_temporary.date_app_positonnow"
                          no-title
                          scrollable
                          locale="th"
                        >
                          <v-spacer></v-spacer>
                          <v-btn text color="primary" @click="menu6 = false">
                            Cancel
                          </v-btn>
                          <v-btn
                            text
                            color="primary"
                            @click="$refs.menu6.save(date)"
                          >
                            OK
                          </v-btn>
                        </v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-container>
                <small>* จำเป็น</small>
                <span>ปีเกษียณ :{{ retrire_year }}</span>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="warning"
                large
                @click.stop="addpersonnel_temporarydialog = false"
                rounded
                ><v-icon dark>mdi-close</v-icon> ยกเลิก</v-btn
              >
              <v-btn
                large
                color="success"
                @click.stop="addpersonnel_temporarySubmit()"
                rounded
              >
                <v-icon dark>mdi-content-save</v-icon>&nbsp;&nbsp;บันทึก
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-layout>

      <!-- V-model csvpersonnel_temporarydialog -->
      <v-layout>
        <v-dialog
          v-model="csvpersonnel_temporarydialog"
          persistent
          max-width="50%"
        >
          <v-card class="mx-auto pa-5">
            <base-material-card
              color="green"
              icon="mdi-content-paste"
              title="นำเข้าข้อมูลไฟล์ CSV"
              class="px-5 py-3 text_google"
            >
            </base-material-card>

            <v-card-text>
              <v-card>
                <v-card-text>
                  <v-form
                    ref="csvpersonnel_temporarydialogform"
                    lazy-validation
                  >
                    <v-container grid-list-md>
                      <v-row>
                        <v-col cols="12">
                          <v-btn
                            block
                            large
                            color="info"
                            to="/college/personnel_form_import/"
                            target="_blank"
                          >
                            <v-icon>mdi-exit-to-app</v-icon
                            >ส่งออกไฟล์บันทึกข้อมูล</v-btn
                          >
                        </v-col>
                        <v-col cols="12" md="12">
                          <v-file-input
                            v-model="file2"
                            accept=".csv"
                            name="csvFiles"
                            color="deep-purple accent-4"
                            counter
                            label="ไฟล์ .csv"
                            placeholder="ไฟล์ CSV ข้อมูลบุคคล สอศ"
                            prepend-icon="mdi-paperclip"
                            outlined
                            rounded
                            :show-size="1000"
                          >
                            <template v-slot:selection="{ index, text }">
                              <v-chip
                                v-if="index < 2"
                                color="deep-purple accent-4"
                                dark
                                label
                                small
                              >
                                {{ text }}
                              </v-chip>

                              <span
                                v-else-if="index === 2"
                                class="text-overline grey--text text--darken-3 mx-2"
                              >
                                +{{ files.length - 2 }} File(s)
                              </span>
                            </template>
                          </v-file-input>
                          <v-btn
                            block
                            large
                            color="green"
                            @click.stop="csvpersonnel_temporarySubmit()"
                          >
                            <v-icon>mdi-book-plus</v-icon>
                            นำเข้า</v-btn
                          >
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-card-text>
            <v-row>
              <v-col cols="12" md="12" class="text-right">
                <v-btn
                  class="mx-2"
                  fab
                  dark
                  large
                  color="grey"
                  @click.stop="csvpersonnel_temporarydialog = false"
                >
                  <v-icon dark>
                    mdi-close
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-dialog>
      </v-layout>

      <!-- V-model csvpersonnel_temporaryAssTeachdialog -->
      <v-layout>
        <v-dialog
          v-model="csvpersonnel_temporaryAssTeachdialog"
          persistent
          max-width="50%"
        >
          <v-card class="mx-auto pa-5">
            <base-material-card
              color="green"
              icon="mdi-content-paste"
              title="นำเข้าข้อมูลไฟล์ CSV (ครูผู้ช่วย) เป็น ครู ค.ศ.1"
              class="px-5 py-3 text_google"
            >
            </base-material-card>

            <v-card-text>
              <v-card>
                <v-card-text>
                  <v-form
                    ref="csvpersonnel_temporaryAssTeachdialogform"
                    lazy-validation
                  >
                    <v-container grid-list-md>
                      <v-row>
                        <v-col cols="12">
                          <v-btn
                            block
                            large
                            color="info"
                            to="/admin/personnel_AssTeachform_import/"
                            target="_blank"
                          >
                            <v-icon>mdi-exit-to-app</v-icon
                            >ส่งออกไฟล์บันทึกข้อมูล</v-btn
                          >
                        </v-col>
                        <v-col cols="12" md="12">
                          <v-file-input
                            v-model="file3"
                            accept=".csv"
                            name="csvFiles"
                            color="deep-purple accent-4"
                            counter
                            label="ไฟล์ .csv"
                            placeholder="ไฟล์ CSV ข้อมูลบุคคล สอศ"
                            prepend-icon="mdi-paperclip"
                            outlined
                            rounded
                            :show-size="1000"
                          >
                            <template v-slot:selection="{ index, text }">
                              <v-chip
                                v-if="index < 2"
                                color="deep-purple accent-4"
                                dark
                                label
                                small
                              >
                                {{ text }}
                              </v-chip>

                              <span
                                v-else-if="index === 2"
                                class="text-overline grey--text text--darken-3 mx-2"
                              >
                                +{{ files.length - 2 }} File(s)
                              </span>
                            </template>
                          </v-file-input>
                          <v-btn
                            block
                            large
                            color="green"
                            @click.stop="csvpersonnel_temporaryTeachAssSubmit()"
                          >
                            <v-icon>mdi-book-plus</v-icon>
                            นำเข้า</v-btn
                          >
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-card-text>
            <v-row>
              <v-col cols="12" md="12" class="text-right">
                <v-btn
                  class="mx-2"
                  fab
                  dark
                  large
                  color="grey"
                  @click.stop="csvpersonnel_temporaryAssTeachdialog = false"
                >
                  <v-icon dark>
                    mdi-close
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-dialog>
      </v-layout>

      <!-- V-model csvpersonnel_temporaryOutofdialog -->
      <v-layout>
        <v-dialog
          v-model="csvpersonnel_temporaryOutofdialog"
          persistent
          max-width="50%"
        >
          <v-card class="mx-auto pa-5">
            <base-material-card
              color="green"
              icon="mdi-content-paste"
              title="นำเข้าข้อมูลไฟล์ ผู้ออกจากราชการ ลาออก เกษียณ ถึงแก่กรรม CSV"
              class="px-5 py-3 text_google"
            >
            </base-material-card>

            <v-card-text>
              <v-card>
                <v-card-text>
                  <v-form
                    ref="csvpersonnel_temporaryOutofdialogform"
                    lazy-validation
                  >
                    <v-container grid-list-md>
                      <v-row>
                        <v-col cols="12">
                          <v-btn
                            block
                            large
                            color="info"
                            to="/admin/personneloutof_form_import/"
                            target="_blank"
                          >
                            <v-icon>mdi-exit-to-app</v-icon
                            >ส่งออกไฟล์บันทึกข้อมูล</v-btn
                          >
                        </v-col>
                        <v-col cols="12" md="12">
                          <v-file-input
                            v-model="file2"
                            accept=".csv"
                            name="csvFiles"
                            color="deep-purple accent-4"
                            counter
                            label="ไฟล์ .csv"
                            placeholder="ไฟล์ CSV ข้อมูลบุคคล สอศ"
                            prepend-icon="mdi-paperclip"
                            outlined
                            rounded
                            :show-size="1000"
                          >
                            <template v-slot:selection="{ index, text }">
                              <v-chip
                                v-if="index < 2"
                                color="deep-purple accent-4"
                                dark
                                label
                                small
                              >
                                {{ text }}
                              </v-chip>

                              <span
                                v-else-if="index === 2"
                                class="text-overline grey--text text--darken-3 mx-2"
                              >
                                +{{ files.length - 2 }} File(s)
                              </span>
                            </template>
                          </v-file-input>
                          <v-btn
                            block
                            large
                            color="green"
                            @click.stop="csvpersonnel_temporaryOutofSubmit()"
                          >
                            <v-icon>mdi-book-plus</v-icon>
                            นำเข้า</v-btn
                          >
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-card-text>
            <v-row>
              <v-col cols="12" md="12" class="text-right">
                <v-btn
                  class="mx-2"
                  fab
                  dark
                  large
                  color="grey"
                  @click.stop="csvpersonnel_temporaryOutofdialog = false"
                >
                  <v-icon dark>
                    mdi-close
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-dialog>
      </v-layout>

      <!-- V-model deletepersonnel_temporarydialog -->
      <v-layout>
        <v-dialog
          v-model="deletepersonnel_temporarydialog"
          persistent
          max-width="40%"
        >
          <v-card class="mx-auto pa-5">
            <base-material-card
              color="error"
              icon="mdi-delete"
              title="ลบข้อมูลผู้ใช้"
              class="px-5 py-3 text_google"
            >
            </base-material-card>

            <v-card-text class="text_google">
              <v-card>
                <v-card-text>
                  <v-form ref="deletepersonnel_temporaryform" lazy-validation>
                    <v-container grid-list-md>
                      <v-layout wrap>
                        <v-flex xs12>
                          ยืนยันการลบข้อมูลผู้ใช้
                          {{ editpersonnel_temporary.college_code }}
                          <span v-if="editpersonnel_temporary.college_name">{{
                            editpersonnel_temporary.college_name
                          }}</span>
                          <div>
                            {{ editpersonnel_temporary.title_s
                            }}{{ editpersonnel_temporary.frist_name }}
                            {{ editpersonnel_temporary.last_name }}
                          </div>
                          <div>
                            {{ editpersonnel_temporary.position_name }} :
                            {{ editpersonnel_temporary.id_position }}
                          </div>
                        </v-flex>
                        <v-flex>
                          <v-select
                            v-model="
                              del_personnel_temporary_del.ptdel_status_del
                            "
                            label="ประเภทการลบ"
                            :items="delPersonneltype"
                            item-text="text"
                            item-value="value"
                            outlined
                            rounded
                            :rules="[v => !!v || '']"
                          ></v-select>
                        </v-flex>
                        <v-flex>
                          <v-menu
                            ref="menu8"
                            v-model="menu8"
                            :close-on-content-click="false"
                            :return-value.sync="date"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                prepend-icon="mdi-calendar-clock"
                                rounded
                                outlined
                                v-model="
                                  del_personnel_temporary_del.ptdel_datetime_outof
                                "
                                label="ออกโดยมีผลวันที่"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                                locale="th"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="
                                del_personnel_temporary_del.ptdel_datetime_outof
                              "
                              no-title
                              scrollable
                              locale="th"
                            >
                              <v-spacer></v-spacer>
                              <v-btn
                                text
                                color="primary"
                                @click="menu8 = false"
                              >
                                Cancel
                              </v-btn>
                              <v-btn
                                text
                                color="primary"
                                @click="$refs.menu8.save(date)"
                              >
                                OK
                              </v-btn>
                            </v-date-picker>
                          </v-menu>
                        </v-flex>

                        <v-text-field
                          filled
                          rounded
                          type="password"
                          label="รหัสผ่าน สำหรับลบข้อมูล"
                          :rules="[v => v === '0612698358']"
                        >
                        </v-text-field>
                      </v-layout>
                    </v-container>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn large @click.stop="deletepersonnel_temporarydialog = false"
                ><v-icon dark>mdi-close</v-icon>ยกเลิก</v-btn
              >
              <v-btn
                large
                color="red darken-3"
                @click.stop="deletepersonnel_temporarySubmit()"
                dark
              >
                <v-icon dark>mdi-delete</v-icon>&nbsp;ลบ
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-layout>

      <!-- V-model editpersonnel_temporarydialog -->
      <v-layout row justify-center>
        <v-dialog
          v-model="editpersonnel_temporarydialog"
          persistent
          max-width="80%"
        >
          <v-card class="mx-auto pa-6">
            <base-material-card
              color="yellow"
              icon="mdi-clipboard-text"
              title="แก้ไขข้อมูลผู้ใช้งานระบบ"
              class="px-5 py-3 text_google"
            ></base-material-card>
            <v-card-text>
              <v-form ref="editpersonnel_temporaryform" lazy-validation>
                <v-row no-gutters>
                  <v-col cols="12" md="12">
                    <v-radio-group v-model="radiosTpyeOganize" row>
                      <template v-slot:label>
                        <div>
                          สังกัด
                          <strong
                            >วิทยาลัย / สถาบัน / หน่วยศึกษานิเทศก์ /
                            ส่วนกลาง</strong
                          >
                        </div>
                      </template>
                      <v-radio value="college">
                        <template v-slot:label>
                          <div>
                            <strong class="success--text">วิทยาลัย</strong>
                          </div>
                        </template>
                      </v-radio>
                      <v-radio value="iven">
                        <template v-slot:label>
                          <div>
                            <strong class="primary--text">สถาบัน</strong>
                          </div>
                        </template>
                      </v-radio>
                      <v-radio value="supervision">
                        <template v-slot:label>
                          <div>
                            <strong class="primary--text"
                              >หน่วยศึกษานิเทศก์</strong
                            >
                          </div>
                        </template>
                      </v-radio>
                      <v-radio value="ovec">
                        <template v-slot:label>
                          <div>
                            <strong class="primary--text">ส่วนกลาง</strong>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                  </v-col>
                  <v-col cols="12" md="12">
                    <hr style="border: 1px solid grey;" />
                  </v-col>

                  <v-col cols="12" md="12">
                    <span class="font-weight-bold">1. ประเภทบุคคล/สังกัด</span>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      rounded
                      filled
                      v-model="editpersonnel_temporary.user_status"
                      :items="userstatus"
                      item-text="user_status_name"
                      item-value="user_status_sub"
                      label="Type"
                      required
                      :rules="[v => !!v || '']"
                      prepend-icon="mdi-format-list-bulleted-type"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="editpersonnel_temporary.college_code"
                      :items="users"
                      item-text="agencys"
                      item-value="user_code"
                      label="วิทยาลัย"
                      @change="man_powerQuery()"
                      required
                      :rules="[v => !!v || '']"
                      prepend-icon="mdi-school"
                      rounded
                      outlined
                    >
                    </v-autocomplete>
                  </v-col>

                  <v-col cols="12" md="12">
                    <hr style="border: 1px solid grey;" />
                  </v-col>
                  <v-col cols="12" md="12"
                    ><span class="font-weight-bold"
                      >2. ข้อมูลพื้นฐาน</span
                    ></v-col
                  >

                  <v-col cols="12" md="3">
                    <v-text-field
                      readonly
                      @input="validateInput()"
                      prepend-icon="mdi-account-key"
                      rounded
                      outlined
                      label="รหัสบัตรประชาชน"
                      v-model="editpersonnel_temporary.id_card"
                      :rules="[
                        v => (!!v && v.length == 13) || '13  ID Card',
                        validateInputcheck === 'true' ||
                          'กำหนดรหัสบัตรประชาชนไม่ถูกต้อง'
                      ]"
                      maxlength="13"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-autocomplete
                      :items="prefixs"
                      item-text="prefix_name"
                      item-value="prefix_name"
                      prepend-icon="mdi-account-box"
                      rounded
                      outlined
                      label="คำนำหน้าชื่อ"
                      v-model="editpersonnel_temporary.title_s"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      prepend-icon="mdi-account-box"
                      rounded
                      outlined
                      label="ชื่อ"
                      v-model="editpersonnel_temporary.frist_name"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      prepend-icon="mdi-account-box"
                      rounded
                      outlined
                      label="นามสกุล"
                      v-model="editpersonnel_temporary.last_name"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-select
                      :items="days"
                      item-text="text"
                      item-value="value"
                      prepend-icon="mdi-calendar"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.brith_day"
                      label="วันที่เกิด : 8"
                    ></v-select>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-select
                      :items="mounts"
                      item-text="text"
                      item-value="value"
                      prepend-icon="mdi-calendar"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.brith_month"
                      label="เดือนเกิด : 8"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      :items="years"
                      item-text="text"
                      item-value="value"
                      prepend-icon="mdi-calendar"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.brith_year"
                      label="ปีเกิด : 2531"
                      type="number"
                      maxlength="4"
                    ></v-autocomplete>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      prepend-icon="mdi-cellphone"
                      rounded
                      outlined
                      label="เบอร์โทร"
                      v-model="editpersonnel_temporary.tel_p"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      prepend-icon="mdi-email"
                      rounded
                      outlined
                      label="E-mail"
                      v-model="editpersonnel_temporary.e_mail"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="12">
                    <hr style="border: 1px solid grey;" />
                  </v-col>
                  <v-col cols="12" md="12"
                    ><span class="font-weight-bold">3. ตำแหน่ง</span></v-col
                  >

                  <v-col cols="12" md="4">
                    <v-autocomplete
                      prepend-icon="mdi-account-settings"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.position_name"
                      :items="position_select"
                      item-text="text"
                      item-value="text"
                      label="ตำแหน่ง"
                    ></v-autocomplete>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-autocomplete
                      prepend-icon="mdi-account-settings"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.user_status"
                      :items="userstatus"
                      item-text="user_status_name"
                      item-value="user_status_sub"
                      label="สถานะ"
                    ></v-autocomplete>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                    v-if="radiosTpyeOganize === 'college'"
                  >
                    <v-autocomplete
                      prepend-icon="mdi-account-settings"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.id_position"
                      :items="man_powers"
                      item-text="id_position"
                      item-value="id_position"
                      label="เลขที่ตำแหน่ง"
                      ><template v-slot:item="{ item }">
                        <div>
                          <div class="font-weight-bold">
                            {{ item.id_position }}
                          </div>
                          <div>{{ item.positions }} : {{ item.id_card }}</div>
                        </div>
                      </template></v-autocomplete
                    >
                  </v-col>

                  <v-col
                    cols="12"
                    md="4"
                    v-if="
                      radiosTpyeOganize === 'iven' ||
                        radiosTpyeOganize === 'supervision' ||
                        radiosTpyeOganize === 'ovec'
                    "
                  >
                    <v-text-field
                      prepend-icon="mdi-account-settings"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.id_position"
                      label="เลขที่ตำแหน่ง"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      prepend-icon="mdi-account-settings"
                      outlined
                      rounded
                      :items="rang_names"
                      item-text="text"
                      item-value="value"
                      label="วิทยฐานะ"
                      v-model="editpersonnel_temporary.rang_name"
                      required
                    ></v-autocomplete>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-select
                      prepend-icon="mdi-account-settings"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.rang_level"
                      :items="rang_levels"
                      item-text="text"
                      item-value="value"
                      label="ระดับ"
                    ></v-select>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      type="number"
                      prepend-icon="mdi-currency-btc"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.salary_s"
                      label="เงินเดือน"
                    ></v-text-field>
                  </v-col>

                  <!-- สาขาวิชาบรรจุ -->
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      prepend-icon="mdi-bookshelf"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.pt_id_branch"
                      :items="branch"
                      item-text="name_branch"
                      item-value="id_branch"
                      label="สาขาวิชาบรรจุ"
                    >
                      <template v-slot:item="{ item }">
                        <div>{{ item.id_branch }} : {{ item.name_branch }}</div>
                      </template>
                    </v-autocomplete>
                  </v-col>

                  <!-- สาขาวิชาที่สอน/เกี่ยวข้อง/ถนัด -->
                  <v-col
                    cols="12"
                    md="6"
                    v-if="
                      editpersonnel_temporary.user_status === 'teacher' ||
                        editpersonnel_temporary.user_status === 'gov_em'
                    "
                  >
                    <v-autocomplete
                      prepend-icon="mdi-teach"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.personnel_tem_branch"
                      :items="rate_work_course_stds"
                      item-text="branch_name_th"
                      item-value="id_course_branch"
                      label="สาขาวิชาที่สอน/เกี่ยวข้อง/ถนัด"
                    ></v-autocomplete>
                  </v-col>

                  <v-col cols="12" md="12">
                    <hr style="border: 1px solid grey;" />
                  </v-col>
                  <v-col cols="12" md="12"
                    ><span class="font-weight-bold"
                      >4. วุฒิการศึกษา</span
                    ></v-col
                  >

                  <v-col cols="12" md="6">
                    <v-text-field
                      prepend-icon="mdi-account-settings"
                      rounded
                      outlined
                      label="คุณวิฒิ"
                      v-model="editpersonnel_temporary.ed_abb"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      prepend-icon="mdi-account-settings"
                      rounded
                      outlined
                      label="สาขาวิชา"
                      v-model="editpersonnel_temporary.ed_name"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" md="12">
                    <hr style="border: 1px solid grey;" />
                  </v-col>
                  <v-col cols="12" md="12"
                    ><span class="font-weight-bold">5. การบรรจุ</span></v-col
                  >

                  <v-col cols="12" md="4">
                    <v-autocomplete
                      prepend-icon="mdi-account-settings"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.type_personnel"
                      :items="type_personnelApp"
                      item-text="text"
                      item-value="value"
                      label="ประเภทการบรรจุ"
                    ></v-autocomplete>
                  </v-col>

                  <v-col cols="12" md="4" class="pa-5">
                    <v-checkbox
                      v-model="editpersonnel_temporary.rang_condition"
                      value="1"
                      hide-details
                      class="shrink mr-2 mt-0"
                      label="กลุ่ม จชต. ชายแดนภาคใต้"
                    ></v-checkbox>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-text-field
                      prepend-icon="mdi-content-duplicate"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.order_app_now"
                      label="คำสั่งที่บรรจุ"
                    ></v-text-field>
                  </v-col>

                  <!-- บรรจะ -->
                  <v-col cols="12" md="4">
                    <v-select
                      :items="days"
                      item-text="text"
                      item-value="value"
                      prepend-icon="mdi-calendar-clock"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.appoin_day"
                      label="วันที่บรรจุ"
                    ></v-select>
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-select
                      :items="mounts"
                      item-text="text"
                      item-value="value"
                      prepend-icon="mdi-calendar-clock"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.appoin_month"
                      label="เดือนบรรจุ"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      :items="years"
                      item-text="text"
                      item-value="value"
                      prepend-icon="mdi-calendar-clock"
                      rounded
                      outlined
                      v-model="editpersonnel_temporary.appoin_year"
                      label="ปีบรรจุ"
                    ></v-autocomplete>
                  </v-col>

                  <v-col cols="12" md="12">
                    <hr style="border: 1px solid grey;" />
                  </v-col>
                  <v-col cols="12" md="12"
                    ><span class="font-weight-bold"
                      >6. การปฏิบัติงาน ณ ปัจจุบัน</span
                    ></v-col
                  >

                  <v-col cols="12" md="6">
                    <v-menu
                      ref="menu5"
                      v-model="menu5"
                      :close-on-content-click="false"
                      :return-value.sync="date"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          prepend-icon="mdi-calendar-clock"
                          rounded
                          outlined
                          v-model="editpersonnel_temporary.date_app_now"
                          label="วันเดือนปีที่เริ่มปฏิบัติงาน ณ สถานศึกษาปัจจุบัน"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          locale="th"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="editpersonnel_temporary.date_app_now"
                        no-title
                        scrollable
                        locale="th"
                      >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="menu5 = false">
                          Cancel
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.menu5.save(date)"
                        >
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-menu
                      ref="menu6"
                      v-model="menu6"
                      :close-on-content-click="false"
                      :return-value.sync="date"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          prepend-icon="mdi-calendar-clock"
                          rounded
                          outlined
                          v-model="editpersonnel_temporary.date_app_positonnow"
                          label="วันเดือนปีที่เริ่มปฏิบัติงาน ใน ตำแหน่งปัจจุบัน"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          locale="th"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="editpersonnel_temporary.date_app_positonnow"
                        no-title
                        scrollable
                        locale="th"
                      >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="menu6 = false">
                          Cancel
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.menu6.save(date)"
                        >
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>

                <small
                  >* *ไม่สามาถแก้ไขเลขบัตรประจำประชาชนได้
                  หากตรวจสอบแล้วพบข้อผิดพลาดในรหัสบัตรประชาชน ให้ติดต่อ
                  Admin</small
                >
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                large
                @click.stop="editpersonnel_temporarydialog = false"
                rounded
              >
                <v-icon dark>mdi-close</v-icon>ยกเลิก
              </v-btn>
              <v-btn
                large
                color="warning"
                @click.stop="editpersonnel_temporarySubmit()"
                rounded
              >
                <v-icon dark>mdi-pencil</v-icon>&nbsp;บันทึก
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-layout>

      <!-- V-model govem_contractdialog -->
      <v-dialog v-model="govem_contractdialog" max-width="80%">
        <v-card class="" elevation="2">
          <embed
            :src="
              'https://hrvec.ovec.go.th/HRemployee_contract/' +
                govem_contract_pdf
            "
            width="100%"
            height="700px"
          />
        </v-card>
      </v-dialog>
    </v-container>

    <v-container fluid>
      <v-snackbar
        v-model="snackbar.show"
        :timeout="snackbar.timeout"
        :color="snackbar.color"
        top
        elevation="2"
      >
        <div class="d-flex align-center">
          <v-icon class="mr-2">{{ snackbar.icon }}</v-icon>
          <span>{{ snackbar.text }}</span>
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            ปิด
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>
<script>
import personnel_bar from "../../components/admin/personnel_bar.vue";
// ApexCharts: npm install apexcharts vue-apexcharts
import VueApexCharts from "vue-apexcharts";
import { impersonationMixin } from "@/mixins/impersonation";

export default {
  name: "personnel_temporary_administrator",
  components: { personnel_bar, apexchart: VueApexCharts },
  mixins: [impersonationMixin],

  data() {
    return {
      loading: false,
      radiosTpyeOganize: "college",
      ApiKey: "HRvec2021",

      // Dialog states
      addpersonnel_temporarydialog: false,
      editpersonnel_temporarydialog: false,
      deletepersonnel_temporarydialog: false,
      csvpersonnel_temporarydialog: false,
      csvpersonnel_temporaryAssTeachdialog: false,
      csvpersonnel_temporaryOutofdialog: false,
      govem_contractdialog: false,

      // Menu states
      menu5: false,
      menu6: false,
      menu8: false,

      // Snackbar
      snackbar: {
        show: false,
        color: "",
        timeout: 5000,
        icon: "",
        text: ""
      },

      // Data arrays
      personnel_temporarys: [],
      personnel_sCounts: [],
      personnel_temporary_idpositions: [],
      users: [],
      userstatus: [],
      man_powers: [],
      prefixs: [],
      veivocals: [],

      // สาขาวิชา (ใหม่)
      branch: [],
      rate_work_course_stds: [],

      // Data objects
      addpersonnel_temporary: {},
      editpersonnel_temporary: {},
      del_personnel_temporary_del: {},
      updateJ18s: {},
      delmanage_assistant_teacher: {},
      data_syslog: {},
      user: {},

      // Search fields
      search: "",
      search_postion: "",
      college_search: {},
      position_search: {},
      rangname_search: {},
      user_status_search: "",
      branch_search: null,
      course_branch_search: null,

      // File uploads
      file2: null,
      file3: null,
      govem_contract_pdf: "",

      // Date
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),

      validateInputcheck: [],

      headers: [
        { text: "Login", align: "center", value: "Login" },
        { text: "แก้ไข", align: "center", value: "actions" },
        { text: "ลบ", align: "center", value: "dels" },
        { text: "Reset Password", value: "reset" },

        { text: "ดูประวัติ", align: "left", value: "viewId" },
        { text: "สัญญาจ้าง", align: "center", value: "govem_contract" },
        { text: "สังกัด", align: "left", value: "agencys" },
        { text: "รหัสบัตรประชาชน", align: "center", value: "id_card" },
        { text: "คำนำ", align: "center", value: "title_s" },
        { text: "ชื่อ", align: "center", value: "frist_name" },
        { text: "สกุล", align: "center", value: "last_name" },
        { text: "ตำแหน่ง", align: "center", value: "position_name" },
        { text: "เลขที่ตำแหน่ง", align: "center", value: "id_position" },
        { text: "วิทยฐานะ", align: "center", value: "rang_name" },
        { text: "ระดับ", align: "center", value: "rang_level" },
        { text: "สาขาวิชาบรรจุ", align: "center", value: "pt_id_branch" },
        {
          text: "สาขาวิชาที่สอน/ถนัด",
          align: "center",
          value: "personnel_tem_branch"
        },
        { text: "วันเดือนปีเกิด", align: "center", value: "brith_day" },
        { text: "วันเดือนปีบรรจุ", align: "center", value: "appoin_day" },
        { text: "คำสั่งที่", align: "center", value: "order_app_now" },
        {
          text: "วันที่ปฏิบัติหน้าที่ ณ สถานศึกษาปัจจุบัน",
          align: "center",
          value: "date_app_now"
        },
        {
          text: "วันที่ดำรงตำแหน่งปัจจุบัน",
          align: "center",
          value: "date_app_positonnow"
        },
        { text: "เกษียณ", align: "center", value: "retrire_year" }
      ],

      // Static data
      delPersonneltype: [
        { text: "ลาออก", value: "resign" },
        { text: "ให้ออก", value: "discard" },
        { text: "ไล่ออก", value: "dismiss" },
        { text: "โอนย้าย", value: "transfer" },
        { text: "เกษียณ", value: "retire" },
        { text: "เสียชีวิต", value: "died" },
        { text: "สละสิทธิ์ไม่รายงานตัว", value: "disclaim" }
      ],

      position_select: [
        { text: "ผู้อำนวยการสถาบัน", value: "ผู้อำนวยการสถาบัน" },
        { text: "ผู้อำนวยการวิทยาลัย", value: "ผู้อำนวยการวิทยาลัย" },
        { text: "รองผู้อำนวยการวิทยาลัย", value: "รองผู้อำนวยการวิทยาลัย" },
        { text: "อาจารย์", value: "อาจารย์" },
        { text: "ผู้ช่วยศาสตราจารย์", value: "ผู้ช่วยศาสตราจารย์" },
        { text: "รองศาสตราจารย์", value: "รองศาสตราจารย์" },
        { text: "ศาสตราจารย์", value: "ศาสตราจารย์" },
        { text: "ครู", value: "ครู" },
        { text: "ครูผู้ช่วย", value: "ครูผู้ช่วย" },
        { text: "ศึกษานิเทศก์", value: "ศึกษานิเทศก์" },
        { text: "เจ้าพนักงานธุรการ", value: "เจ้าพนักงานธุรการ" },
        {
          text: "เจ้าพนักงานการเงินและบัญชี",
          value: "เจ้าพนักงานการเงินและบัญชี"
        },
        { text: "นักวิเคราะห์นโยบายและแผน", value: "นักวิเคราะห์นโยบายและแผน" },
        { text: "นักวิชาการเงินและบัญชี", value: "นักวิชาการเงินและบัญชี" },
        { text: "เจ้าพนักงานพัสดุ", value: "เจ้าพนักงานพัสดุ" }
      ],

      type_personnelApp: [
        { text: "ครูพัฒนาท้องถิ่น", value: "local_dev" },
        { text: "ครูโอนย้าย", value: "transfer_position" },
        { text: "คัดเลือก(ภายใน)", value: "select" },
        { text: "แข่งขัน(ภายนอก)", value: "recruit" }
      ],

      rang_names: [
        { text: "ต้น", value: "ต้น" },
        { text: "ปฏิบัติงาน", value: "ปฏิบัติงาน" },
        { text: "ปฏิบัติการ", value: "ปฏิบัติการ" },
        { text: "ครูผู้ช่วย", value: "ครูผู้ช่วย" },
        { text: "ครู", value: "ครู" },
        { text: "ชำนาญงาน", value: "ชำนาญงาน" },
        { text: "ชำนาญการ", value: "ชำนาญการ" },
        { text: "ชำนาญการพิเศษ", value: "ชำนาญการพิเศษ" },
        { text: "เชี่ยวชาญ", value: "เชี่ยวชาญ" },
        { text: "เชี่ยวชาญพิเศษ", value: "เชี่ยวชาญพิเศษ" }
      ],

      rang_levels: [
        { text: "ครูผู้ช่วย", value: "ครูผู้ช่วย" },
        { text: "1", value: "1" },
        { text: "2", value: "2" },
        { text: "3", value: "3" },
        { text: "4", value: "4" },
        { text: "5", value: "5" }
      ],

      days: Array.from({ length: 31 }, (_, i) => ({
        text: String(i + 1),
        value: String(i + 1)
      })),

      mounts: Array.from({ length: 12 }, (_, i) => ({
        text: String(i + 1),
        value: String(i + 1)
      })),

      years: Array.from({ length: 71 }, (_, i) => {
        const year = 2500 + i;
        return { text: String(year), value: String(year) };
      }),
      data_syslog: {
        ApiKey: "",
        user_account: "",
        event_log: "",
        page_log: "",
        table_log: "",
        detail_log: "",
        date_times: ""
      },
      validateInputcheck: "false",
      exportingExcel: false, // ⭐ ใหม่: สถานะ loading ของปุ่ม export
      exportingType: null,
      personnel_sHeaders: [
        { text: "ประเภท", align: "center", value: "position_name" },
        { text: "จำนวน", align: "center", value: "count_grouptype" },
        {
          text: "สัดส่วน (%)",
          align: "center",
          value: "percent",
          sortable: false
        } // ⭐ ใหม่
      ]
    };
  },

  async mounted() {
    await this.initializeData();
  },

  computed: {
    personnel_sCountsWithPercent() {
      const total = this.totalPersonnelCount || 0;
      return (this.personnel_sCounts || []).map(item => {
        const count = Number(item.count_grouptype) || 0;
        const percent =
          total > 0 ? Number(((count / total) * 100).toFixed(1)) : 0;
        return { ...item, percent };
      });
    },
    filteredHeaders() {
      return this.user.userauthority_govem === "1"
        ? this.headers.filter(h => h.value !== "ac")
        : this.headers;
    },

    time_stamp() {
      return new Date().getTime();
    },

    retrire_year() {
      const mm = this.brith_month;
      const yy_retire = this.brith_year;
      return mm > 9 ? yy_retire + 61 : yy_retire + 60;
    },

    date_today_log() {
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const yyyy = today.getFullYear() + 543;
      const time = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;
      return `${dd}/${mm}/${yyyy}/${time}`;
    },

    // ==================== ตรวจจับรูปแบบการค้นหาอัตโนมัติ ====================
    detectedSearchType() {
      const val = (this.search || "").toString().trim();
      if (!val) return "";
      if (/^\d{13}$/.test(val)) return "รหัสบัตรประชาชน";
      if (/^25\d{2}$/.test(val)) return "ปีเกษียณ (พ.ศ.)";
      if (/^\d+$/.test(val)) return "เลขที่ตำแหน่ง";
      return "ชื่อ / นามสกุล";
    },

    // ==================== ข้อมูลสำหรับ Dashboard ====================
    totalPersonnelCount() {
      return this.personnel_sCounts.reduce(
        (sum, i) => sum + (Number(i.count_grouptype) || 0),
        0
      );
    },

    donutChartSeries() {
      return this.personnel_sCounts.map(i => Number(i.count_grouptype) || 0);
    },

    donutChartOptions() {
      return {
        labels: this.personnel_sCounts.map(i => i.position_name || "ไม่ระบุ"),
        legend: { position: "bottom", fontSize: "13px" },
        dataLabels: { enabled: true },
        colors: [
          "#1976D2",
          "#26A69A",
          "#FFA726",
          "#EF5350",
          "#7E57C2",
          "#66BB6A",
          "#EC407A",
          "#8D6E63",
          "#42A5F5",
          "#FF7043"
        ],
        responsive: [
          {
            breakpoint: 600,
            options: {
              chart: { width: "100%" },
              legend: { position: "bottom" }
            }
          }
        ]
      };
    },

    // จำนวนบุคลากรตามวิทยฐานะ (คำนวณจากรายการปัจจุบันในตาราง)
    rangCountMap() {
      const map = {};
      (this.personnel_temporarys || []).forEach(item => {
        const key =
          item.rang_name && item.rang_name.trim() ? item.rang_name : "ไม่ระบุ";
        map[key] = (map[key] || 0) + 1;
      });
      return map;
    },

    rangBarSeries() {
      return [{ name: "จำนวน (คน)", data: Object.values(this.rangCountMap) }];
    },

    rangBarOptions() {
      return {
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        dataLabels: { enabled: true },
        xaxis: { categories: Object.keys(this.rangCountMap) },
        colors: ["#1976D2"]
      };
    }
  },

  methods: {
    async exportPersonnelByTypeToExcel(item) {
      if (!item || !item.position_name) return;

      this.exportingType = item.position_name;
      await this.sweetAlertLoading();

      try {
        const result = await this.$http.post("personnel_temporary_ex.php", {
          ApiKey: this.ApiKey,
          position_name: item.position_name
        });

        const list = result.data || [];
        if (!list.length) {
          Swal.close();
          this.showError("ไม่พบข้อมูลสำหรับตำแหน่งนี้");
          return;
        }

        const XLSX = await import("xlsx");

        const rows = list.map((p, idx) => ({
          ลำดับ: idx + 1,
          ภาค: p.region_name || "",
          จังหวัด: p.province_name || "",
          รหัสวิทยาลัย: p.college_code || "",
          วิทยาลัย: p.agencys || "",
          รหัสบัตรประชาชน: p.id_card || "",
          "ชื่อ-นามสกุล": p.frist_names || "",
          ตำแหน่ง: p.position_name || "",
          เลขที่ตำแหน่ง: p.id_position || "",
          เงินเดือน: p.salary_s || "",
          ประเภทตำแหน่ง: p.type_position || "",
          วิทยฐานะ: p.rang_name || "",
          ระดับ: p.rang_level || "",
          วุฒิการศึกษา: p.ed_abb || "",
          สาขาวิชา: p.ed_name || "",
          สอนสาขาวิชา: p.branch_name_th || "",
          วันเกิด: p.brith_day || "",
          เดือนเกิด: p.brith_month || "",
          ปีเกิด: p.brith_year || "",
          วันบรรจุ: p.appoin_day || "",
          เดือนบรรจุ: p.appoin_month || "",
          ปีบรรจุ: p.appoin_year || "",
          คำสั่ง: p.order_app_now || "",
          ปฏิบัติงานวันที่: p.date_app_now || "",
          "ปฏิบัติ ณ สถานศึกษาปัจจุบัน": p.date_app_positonnow || "",
          เกษียณ: p.retrire_year || "",
          "ใบอนุญาต (ครู)": p.pt_licence || "",
          "วันที่ออก (ครู)": p.pt_ondate || "",
          "วันหมด (ครู)": p.pt_enddate || "",
          "ใบอนุญาต (ผู้บริหาร)": p.pt_licencedr || "",
          "วันที่ออก (ผู้บริหาร)": p.pt_ondatedr || "",
          "วันหมด (ผู้บริหาร)": p.pt_enddatedr || "",
          เบอร์โทร: p.tel_p || ""
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        worksheet["!cols"] = Object.keys(rows[0]).map(() => ({ wch: 18 }));

        const workbook = XLSX.utils.book_new();
        // ชื่อชีตห้ามเกิน 31 ตัวอักษร (ข้อจำกัดของไฟล์ Excel)
        const sheetName =
          String(item.position_name).substring(0, 31) || "Sheet1";
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        const today = this.formatDate(new Date());
        XLSX.writeFile(workbook, `รายชื่อ_${item.position_name}_${today}.xlsx`);

        await this.writeSyslog(
          "export_excel",
          "personnel_temporary_by_type",
          item.position_name
        );

        Swal.close();
      } catch (err) {
        console.error("Export by type error:", err);
        Swal.close();
        this.showError("ไม่สามารถส่งออกไฟล์ได้");
      } finally {
        this.exportingType = null;
      }
    },
    // ==================== สาขาวิชา (ใหม่) ====================
    async loadBranch() {
      const result = await this.$http.post("branch.php", {
        ApiKey: this.ApiKey
      });
      this.branch = result.data || [];
    },

    async loadRateWorkCourseStd() {
      const result = await this.$http.post("rate_work_course_std.php", {
        ApiKey: this.ApiKey
      });
      this.rate_work_course_stds = result.data || [];
    },

    getBranchName(id) {
      if (!id) return "-";
      const found = this.branch.find(b => b.id_branch === id);
      return found ? `${found.id_branch} : ${found.name_branch}` : id;
    },

    getCourseBranchName(id) {
      if (!id) return "-";
      const found = this.rate_work_course_stds.find(
        b => b.id_course_branch === id
      );
      return found ? found.branch_name_th : id;
    },

    async search_branch() {
      await this.searchPersonnel({ pt_id_branch: this.branch_search });
    },

    async search_course_branch() {
      await this.searchPersonnel({
        personnel_tem_branch: this.course_branch_search
      });
    },

    async personnel_etcReset(id_card) {
      const result = await Swal.fire({
        title: "Reset Password",
        text:
          "ระบบจะสุ่มรหัสผ่านใหม่ให้บุคลากรคนนี้ และบังคับให้เปลี่ยนรหัสผ่านทันทีที่เข้าสู่ระบบครั้งถัดไป",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก"
      });
      if (!result.isConfirmed) return;

      // ไม่ส่งรหัสผ่านจากฝั่งนี้อีกต่อไป — backend สุ่มรหัสผ่านเองและตรวจสิทธิ์จาก
      // session ที่ login อยู่จริงเท่านั้น (บัญชี A ผู้ดูแลระบบ reset ได้ทุกคนไม่จำกัดสถานศึกษา)
      let res = await this.$http.post("personnel_etc.update.pass.php", {
        ApiKey: this.ApiKey,
        admin_reset: true,
        personnel_etc_id_card: id_card
      });
      if (res.data.status == true || res.data.status == "true") {
        const newPassword = res.data.new_password;
        await this.writeSyslog(
          "admin_reset_password",
          "personnel_etc.update.pass",
          id_card
        );

        await Swal.fire({
          title: "รีเซ็ตรหัสผ่านสำเร็จ",
          icon: "success",
          html:
            `<p>รหัสผ่านใหม่สำหรับบุคลากร (เลขบัตร ${id_card}):</p>` +
            `<div style="font-size:22px;font-weight:bold;letter-spacing:2px;` +
            `background:#f2fbef;border:1px dashed #7cea66;border-radius:8px;` +
            `padding:10px;margin:10px 0;user-select:all;">${newPassword}</div>` +
            `<p style="font-size:13px;color:#888;">กรุณาคัดลอกแล้วแจ้งบุคลากรทันที ระบบจะไม่แสดงรหัสนี้อีก</p>`,
          confirmButtonText: "คัดลอกรหัสผ่าน",
          showCancelButton: true,
          cancelButtonText: "ปิด"
        }).then(async copyResult => {
          if (copyResult.isConfirmed) {
            try {
              await navigator.clipboard.writeText(newPassword);
              Swal.fire({
                icon: "success",
                title: "คัดลอกแล้ว",
                showConfirmButton: false,
                timer: 1200
              });
            } catch (e) {
              Swal.fire({
                icon: "error",
                title: "คัดลอกอัตโนมัติไม่สำเร็จ กรุณาคัดลอกด้วยตนเอง"
              });
            }
          }
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "ไม่สามารถรีเซ็ตรหัสผ่านได้",
          text: res.data.message || ""
        });
      }
    },

    async writeSyslog(eventLog, tableLog, detailLog) {
      let userSession = JSON.parse(localStorage.getItem("user")) || 0;
      this.data_syslog.ApiKey = this.ApiKey;
      this.data_syslog.user_account = userSession.user_name;
      this.data_syslog.event_log = eventLog;
      this.data_syslog.page_log = "personnel_etc";
      this.data_syslog.table_log = tableLog;
      this.data_syslog.detail_log = detailLog;
      this.data_syslog.date_times = this.date_today_log;
      await this.$http.post("data_syslog.insert.php", this.data_syslog);
    },

    // Initialize
    async initializeData() {
      try {
        await this.userQueryAll();
        await this.sweetAlertLoading();

        await Promise.all([
          this.loadUsers(),
          this.loadUserStatus(),
          this.loadPrefixes(),
          this.loadVeivocals(),
          this.loadBranch(),
          this.loadRateWorkCourseStd(),
          this.personnel_temporaryCount()
          //.personnel_temporaryQueryAll()
        ]);

        Swal.close();
      } catch (error) {
        console.error("Error initializing data:", error);
        this.showError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
      }
    },

    async loadUsers() {
      const result = await this.$http.post("user.php", {
        ApiKey: this.ApiKey,
        admin: "Ok"
      });
      // dropdown "สถานศึกษา" ทั้งหมดในหน้านี้ผูกกับ item-text="agencys" ซึ่ง backend
      // สร้างจาก user_firstname/user_lastname ต่อกัน (ไม่ใช่ college_name จริง) — ถ้าบัญชี
      // ของสถานศึกษาไหน firstname/lastname ว่างหรือไม่ตรงกับชื่อจริง จะค้นหาด้วยชื่อสถานศึกษา
      // ไม่เจอทั้งที่มีข้อมูลอยู่จริงในตาราง college จึงใช้ college_name (ที่ query เดียวกัน join
      // มาให้อยู่แล้ว) เป็นหลักแทน โดย fallback ไป agencys เดิมสำหรับบัญชีที่ไม่มีวิทยาลัยผูกอยู่
      this.users = (result.data || []).map(u => ({
        ...u,
        agencys: u.college_name || u.agencys
      }));
    },

    async loadUserStatus() {
      const result = await this.$http.post("user_status.php", {
        ApiKey: this.ApiKey
      });
      this.userstatus = result.data;
    },

    async loadPrefixes() {
      const result = await this.$http.post("prefix.php", {
        ApiKey: this.ApiKey
      });
      this.prefixs = result.data;
    },

    async loadVeivocals() {
      const result = await this.$http.post("veivocal.php", {
        ApiKey: this.ApiKey
      });
      this.veivocals = result.data;
    },

    // User
    async userQueryAll() {
      const userSession = JSON.parse(localStorage.getItem("user")) || {};
      const result = await this.$http.post("user.php", {
        ApiKey: this.ApiKey,
        user_ID: userSession.user_ID
      });
      this.user = result.data;
    },

    // สวมสิทธิ์เข้าใช้งานแทนบัญชีนี้ — ใช้ mixin กลาง (ดู HRvec/src/mixins/impersonation.js)
    // ที่ผ่าน session แอดมินจริง (admin.impersonate_start.php) และบันทึก audit log แทนกลไกเดิม
    // login.php?crud=userByAdmin ซึ่งไม่ตรวจสิทธิ์ใดๆ เลยและพังไปแล้วตั้งแต่ personnel_temporary.php
    // เลิกส่ง p_word (hash) กลับมาให้ client (ถูกลบแล้ว)
    async userLogin(id_card, displayName) {
      await this.startImpersonation({
        subjectType: "personnel_temporary",
        subjectId: id_card,
        displayName
      });
    },

    // Search methods
    async searchPersonnel(params) {
      // ถ้ามีการระบุ key ตัวกรองมา แต่ค่ากลับว่างเปล่า (เช่น กด "x" ล้างค่าบน autocomplete
      // ตัวใดตัวหนึ่ง — college_search/position_search/rangname_search ฯลฯ) ห้ามยิง request เลย
      // เพราะ personnel_temporary.php จะตีความว่า "ไม่มีตัวกรองเลย" แล้วคืนข้อมูลทั้งตาราง
      // (นับหมื่นแถวทั่วประเทศ พร้อม correlated subquery ~6 จุดต่อแถว) ซึ่งหนักมากจน request
      // ค้าง/timeout บน production ทำให้ browser ขึ้น error CORS/Network Error เพราะไม่มี
      // response กลับมาเลย — ปุ่ม "แสดงทั้งหมด" (personnel_temporaryQueryAll) ที่ตั้งใจส่ง {}
      // (ไม่มี key เลย) ยังคงทำงานได้ตามปกติ ไม่ถูกบล็อกโดยเงื่อนไขนี้
      const keys = Object.keys(params);
      const hasEmptyFilterValue = keys.some(
        k => params[k] === null || params[k] === undefined || params[k] === ""
      );
      if (keys.length > 0 && hasEmptyFilterValue) {
        this.personnel_temporarys = [];
        return;
      }

      await this.sweetAlertLoading();
      this.loading = true;

      try {
        const result = await this.$http.post("personnel_temporary.php", {
          ApiKey: this.ApiKey,
          ...params
        });
        this.personnel_temporarys = result.data;
      } finally {
        this.loading = false;
        Swal.close();
      }
    },

    // ==================== ค้นหาแบบอัจฉริยะ (รองรับหลายรูปแบบ) ====================
    // พิมพ์คำเดียวในช่องค้นหาหลัก ระบบจะตรวจจับรูปแบบและเลือกเงื่อนไขค้นหาที่เหมาะสมให้อัตโนมัติ:
    //  - ตัวเลข 13 หลัก        -> ค้นหาด้วยรหัสบัตรประชาชน
    //  - ตัวเลข 4 หลัก 25xx    -> ค้นหาด้วยปีเกษียณ (พ.ศ.)
    //  - ตัวเลขล้วน (รูปแบบอื่น) -> ค้นหาด้วยเลขที่ตำแหน่ง
    //  - ข้อความ               -> ค้นหาด้วยชื่อ/นามสกุล (พร้อมกัน)
    async OnEnter() {
      const val = (this.search || "").toString().trim();
      if (!val) {
        // await this.personnel_temporaryQueryAll();
        return;
      }

      if (/^\d{13}$/.test(val)) {
        await this.searchPersonnel({ id_cards: val });
      } else if (/^25\d{2}$/.test(val)) {
        await this.searchPersonnel({ retrire_year: val });
      } else if (/^\d+$/.test(val)) {
        await this.searchPersonnel({ id_position: val });
      } else {
        await this.searchPersonnel({ frist_name: val, last_name: val });
      }
    },

    async search_name() {
      await this.searchPersonnel({ frist_name: this.search });
    },

    async search_lastname() {
      await this.searchPersonnel({ last_name: this.search });
    },

    async search_idcard() {
      await this.searchPersonnel({ id_cards: this.search });
    },

    async search_idposition() {
      await this.searchPersonnel({ id_position: this.search });
    },

    async search_idpositionClick(id_position) {
      await this.searchPersonnel({ id_position });
    },

    async search_college() {
      await this.searchPersonnel({ college_code: this.college_search });
    },

    async search_position() {
      await this.searchPersonnel({ position_name: this.position_search });
    },

    async search_rang() {
      await this.searchPersonnel({ rang_name: this.rangname_search });
    },

    async search_yearretire() {
      await this.searchPersonnel({ retrire_year: this.search });
    },

    async search_user_status() {
      await this.searchPersonnel({ user_status: this.user_status_search });
    },

    async clearSearch() {
      this.search = "";
      this.college_search = "";
      this.position_search = "";
      this.rangname_search = "";
      this.user_status_search = "";
      this.branch_search = null;
      this.course_branch_search = null;
    },

    // Personnel operations
    async personnel_temporaryCount() {
      const result = await this.$http.post("personnel_temporary.php", {
        ApiKey: this.ApiKey,
        countTypePerson: "Ok"
      });
      this.personnel_sCounts = result.data;
    },

    async personnel_temporaryQueryAll() {
      await this.searchPersonnel({});
    },

    personnel_temporaryAdd() {
      this.addpersonnel_temporary = {};
      this.addpersonnel_temporarydialog = true;
    },

    personnel_temporaryCSV() {
      this.csvpersonnel_temporarydialog = true;
    },

    personnel_temporaryAssTeachCSV() {
      this.csvpersonnel_temporaryAssTeachdialog = true;
    },

    personnel_temporaryOutofCSV() {
      this.csvpersonnel_temporaryOutofdialog = true;
    },

    async personnel_temporaryEdit(id_card) {
      await this.sweetAlertLoading();

      const result = await this.$http.post("personnel_temporary.php", {
        ApiKey: this.ApiKey,
        id_card
      });

      this.editpersonnel_temporary = result.data;
      this.editpersonnel_temporary.p_word = "";

      // ✅ เพิ่มตรงนี้ - แปลงค่าให้เป็น String ให้ตรงกับ items ใน v-select
      this.editpersonnel_temporary.brith_day = String(
        this.editpersonnel_temporary.brith_day || ""
      );
      this.editpersonnel_temporary.brith_month = String(
        this.editpersonnel_temporary.brith_month || ""
      );
      this.editpersonnel_temporary.brith_year = String(
        this.editpersonnel_temporary.brith_year || ""
      );

      // แปลง appoin ด้วยถ้าใช้ v-select เช่นกัน
      this.editpersonnel_temporary.appoin_day = String(
        this.editpersonnel_temporary.appoin_day || ""
      );
      this.editpersonnel_temporary.appoin_month = String(
        this.editpersonnel_temporary.appoin_month || ""
      );
      this.editpersonnel_temporary.appoin_year = String(
        this.editpersonnel_temporary.appoin_year || ""
      );

      if (this.editpersonnel_temporary.college_code) {
        await this.man_powerQuery();
      }

      this.validateInput();
      this.editpersonnel_temporarydialog = true;
      Swal.close();
    },

    async personnel_temporaryDelete(id_card) {
      const result = await this.$http.post("personnel_temporary.php", {
        ApiKey: this.ApiKey,
        id_card
      });
      this.editpersonnel_temporary = result.data;
      this.deletepersonnel_temporarydialog = true;
    },

    // Submit methods
    async addpersonnel_temporarySubmit() {
      if (!this.$refs.addpersonnel_temporaryform.validate()) return;

      this.addpersonnel_temporary.ApiKey = this.ApiKey;
      this.addpersonnel_temporary.p_word = `${this.addpersonnel_temporary.brith_day}/${this.addpersonnel_temporary.brith_month}/${this.addpersonnel_temporary.brith_year}`;

      const result = await this.$http.post(
        "personnel_temporary.insert.php",
        this.addpersonnel_temporary
      );

      if (result.data.status) {
        this.showSuccess("ดำเนินการบันทึกข้อมูลเรียบร้อย");
        await this.logAction("insert", result.data.id_card);
      } else {
        this.showError("บันทึกข้อมูลผิดพลาด");
      }

      this.addpersonnel_temporarydialog = false;
    },

    async editpersonnel_temporarySubmit() {
      if (!this.$refs.editpersonnel_temporaryform.validate()) return;

      this.editpersonnel_temporary.ApiKey = this.ApiKey;

      if (!this.editpersonnel_temporary.p_word) {
        delete this.editpersonnel_temporary.p_word;
      }

      if (!this.editpersonnel_temporary.rang_condition) {
        this.editpersonnel_temporary.rang_condition = "0";
      }

      const result = await this.$http.post(
        "personnel_temporary.update.admin.php",
        this.editpersonnel_temporary
      );

      if (result.data.status) {
        this.showSuccess("ดำเนินการแก้ไขข้อมูลเรียบร้อย");
        await this.UpdateJ18();
        await this.logAction("update", result.data.id_card);
      } else {
        this.showError("ดำเนินการผิดพลาด");
      }

      this.editpersonnel_temporarydialog = false;
    },

    async deletepersonnel_temporarySubmit() {
      if (!this.$refs.deletepersonnel_temporaryform.validate()) return;

      await this.prepareDeleteData();

      const [
        resultIns,
        resultDel,
        resultJ18,
        resultDelMan
      ] = await Promise.all([
        this.$http.post(
          "personnel_temporary_del.insert.php",
          this.del_personnel_temporary_del
        ),
        this.$http.post(
          "personnel_temporary.delete.php",
          this.editpersonnel_temporary
        ),
        this.$http.post("jor18.updateIdPosition.php", this.updateJ18s),
        this.$http.post(
          "manage_assistant_teacher.deleteidcardandidpos.php",
          this.delmanage_assistant_teacher
        )
      ]);

      console.log("Delete Result:", {
        resultIns: resultIns.data,
        resultDel: resultDel.data,
        resultJ18: resultJ18.data,
        resultDelMan: resultDelMan.data
      });

      if (resultDel.data.status && resultJ18.data.status) {
        this.showSuccess("ดำเนินการลบข้อมูลเรียบร้อย");
        await this.UpdateJ18();
        await this.logAction("delete", resultDel.data.id_card);
        this.clearSearch();
      } else {
        this.showError("ดำเนินการลบผิดพลาด");
      }

      this.deletepersonnel_temporarydialog = false;
    },

    async prepareDeleteData() {
      this.editpersonnel_temporary.ApiKey = this.ApiKey;

      Object.assign(this.del_personnel_temporary_del, {
        ApiKey: this.ApiKey,
        ptdel_college_code: this.editpersonnel_temporary.college_code,
        ptdel_id_card: this.editpersonnel_temporary.id_card,
        ptdel_title_s: this.editpersonnel_temporary.title_s,
        ptdel_frist_name: this.editpersonnel_temporary.frist_name,
        ptdel_last_name: this.editpersonnel_temporary.last_name,
        ptdel_position_name: this.editpersonnel_temporary.position_name,
        ptdel_id_position: this.editpersonnel_temporary.id_position,
        ptdel_salary_s: this.editpersonnel_temporary.salary_s,
        ptdel_rang_name: this.editpersonnel_temporary.rang_name,
        ptdel_rang_level: this.editpersonnel_temporary.rang_level,
        ptdel_ed_abb: this.editpersonnel_temporary.ed_abb,
        ptdel_ed_name: this.editpersonnel_temporary.ed_name,
        ptdel_brith_day: this.editpersonnel_temporary.brith_day,
        ptdel_brith_month: this.editpersonnel_temporary.brith_month,
        ptdel_brith_year: this.editpersonnel_temporary.brith_year,
        ptdel_appoin_day: this.editpersonnel_temporary.appoin_day,
        ptdel_appoin_month: this.editpersonnel_temporary.appoin_month,
        ptdel_appoin_year: this.editpersonnel_temporary.appoin_year,
        ptdel_retire_year: this.editpersonnel_temporary.retrire_year,
        ptdel_datetime: this.date_today_log
      });

      const delTypeMap = {
        resign: "ลาออก",
        discard: "ให้ออก",
        dismiss: "ไล่ออก",
        transfer: "โอนย้าย",
        retire: "เกษียณ",
        died: "เสียชีวิต",
        disclaim: "สละสิทธิ์ไม่รายงานตัว"
      };

      const j18_background =
        delTypeMap[this.del_personnel_temporary_del.ptdel_status_del];

      Object.assign(this.updateJ18s, {
        ApiKey: this.ApiKey,
        id_position: this.editpersonnel_temporary.id_position,
        positions: this.editpersonnel_temporary.position_name,
        j18_background: `${j18_background}:${this.del_personnel_temporary_del.ptdel_datetime_outof}`
      });

      Object.assign(this.delmanage_assistant_teacher, {
        ApiKey: this.ApiKey,
        mt_id_card: this.editpersonnel_temporary.id_card,
        mt_id_position: this.editpersonnel_temporary.id_position
      });
    },

    // CSV Import methods
    async csvpersonnel_temporarySubmit() {
      await this.uploadCSV(
        this.file2,
        "personnel.csv",
        "personnel_importcsv.php"
      );
      this.csvpersonnel_temporarydialog = false;
    },

    async csvpersonnel_temporaryTeachAssSubmit() {
      await this.uploadCSV(
        this.file3,
        "personnel.csv",
        "personnel_importTeachAsscsv.php"
      );
      this.csvpersonnel_temporaryAssTeachdialog = false;
    },

    async csvpersonnel_temporaryOutofSubmit() {
      await this.uploadCSV(
        this.file2,
        "personnelretried.csv",
        "personnel_importretriedcsv.php"
      );
      this.csvpersonnel_temporaryOutofdialog = false;
    },

    async uploadCSV(file, filenamePrefix, endpoint) {
      if (!file) return;

      await this.sweetAlertLoading();

      const formData = new FormData();
      formData.append("file", file);
      formData.append(
        "filename",
        `../HRvecfiles/${this.time_stamp}.${filenamePrefix}`
      );
      formData.append("ApiKey", this.ApiKey);

      try {
        const result = await this.$http.post(endpoint, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });

        if (result.data.status) {
          this.showSuccess("ดำเนินการบันทึกข้อมูลเรียบร้อย");
          await this.UpdateJ18();
        } else {
          this.showError("ดำเนินการผิดพลาด");
        }
      } finally {
        Swal.close();
      }
    },

    // Utility methods
    async UpdateJ18() {
      await this.sweetAlertLoading();

      const data = { ApiKey: this.ApiKey };
      const [resulta, resultb] = await Promise.all([
        this.$http.post("jor18.update.php", data),
        this.$http.post("jor18.update.admin.php", data)
      ]);

      if (resulta.data.status && resultb.data.status) {
        this.showSuccess("ดำเนินการบันทึกข้อมูลเรียบร้อย");
      } else {
        this.showError("ดำเนินการผิดพลาด");
      }

      Swal.close();
    },

    async man_powerQuery() {
      const datasearch =
        this.editpersonnel_temporary.college_code ||
        this.addpersonnel_temporary.college_code;

      if (datasearch) {
        const result = await this.$http.post("jor18.php", {
          ApiKey: this.ApiKey,
          j18college_code: datasearch
        });
        this.man_powers = result.data;
      }
    },

    async clearRetireSearch() {
      const result = await Swal.fire({
        title: "ต้องการลบผู้เกษียณอายุราชการปี",
        text: this.search,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก"
      });

      if (!result.isConfirmed) return;

      const data = {
        ApiKey: this.ApiKey,
        retrire_year: this.search
      };

      try {
        const resultIns = await this.$http.post(
          "personnel_temporary.retire.update.php",
          data
        );

        if (resultIns.data.status) {
          const resultDel = await this.$http.post(
            "personnel_temporary.retire.delete.php",
            data
          );

          if (resultDel.data.status) {
            this.showSuccess("ดำเนินการข้อมูลเรียบร้อย");
          } else {
            this.showError("ผิดพลาด");
          }
        } else {
          this.showError("ผิดพลาด");
        }
      } catch (error) {
        this.showError("เกิดข้อผิดพลาด");
      }
    },

    async repleteSearch() {
      await this.sweetAlertLoading();
      const result = await this.$http.post("personnel_temporary.php", {
        ApiKey: this.ApiKey,
        repetitive: "ok"
      });
      this.personnel_temporary_idpositions = result.data;
      Swal.close();
    },

    govem_contract_showDialog(govem_contract) {
      this.govem_contract_pdf = govem_contract;
      this.govem_contractdialog = true;
    },

    // Validation
    validateInputAdd() {
      this.validateInputcheck = this.validateIdCard(
        this.addpersonnel_temporary.id_card
      );
    },

    validateInput() {
      this.validateInputcheck = this.validateIdCard(
        this.editpersonnel_temporary.id_card
      );
    },

    validateIdCard(value) {
      if (!value || value.length !== 13 || !/^\d+$/.test(value)) {
        return "false";
      }

      let sum = 0;
      for (let i = 0; i < 12; i++) {
        sum += parseInt(value.charAt(i)) * (13 - i);
      }

      const check = (11 - (sum % 11)) % 10;
      return check === parseInt(value.charAt(12)) ? "true" : "false";
    },

    // Alert helpers
    async sweetAlertLoading() {
      Swal.fire({
        title: "Please Wait !",
        timerProgressBar: true,
        didOpen: () => Swal.showLoading(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false
      });
    },

    showSuccess(message) {
      Swal.fire({
        icon: "success",
        title: message,
        showConfirmButton: false,
        timer: 1500
      });
    },

    showError(message) {
      Swal.fire({
        icon: "warning",
        title: message,
        showConfirmButton: false,
        timer: 1500
      });
    },

    showSnackbar(text, color = "success", icon = "mdi-check") {
      this.snackbar = {
        show: true,
        text,
        color,
        icon,
        timeout: 5000
      };
    },

    // Logging
    async logAction(eventType, id_card) {
      const userSession = JSON.parse(localStorage.getItem("user")) || {};

      const logData = {
        ApiKey: this.ApiKey,
        user_account: userSession.user_name,
        event_log: eventType,
        page_log: "personnel_temporary",
        table_log: "personnel_temporary",
        detail_log: id_card,
        date_times: this.date_today_log
      };

      await this.$http.post("data_syslog.insert.php", logData);
    },

    // Date calculations
    calculateBirthDate(dateString) {
      if (!dateString) return { day: "", month: "", year: "" };

      const date = new Date(dateString);
      return {
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear() + 543
      };
    },

    calculateAge(birthDate) {
      if (!birthDate) return "";

      const today = new Date();
      const dob = new Date(birthDate);

      let years = today.getFullYear() - dob.getFullYear();
      let months = today.getMonth() - dob.getMonth();
      let days = today.getDate() - dob.getDate();

      if (days < 0) {
        months--;
        const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        days += lastMonth.getDate();
      }

      if (months < 0) {
        years--;
        months += 12;
      }

      return `${years} ปี ${months} เดือน ${days} วัน`;
    },

    formatDate(date, addYears = 543) {
      if (!date) return "";

      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear() + addYears;

      return `${day}/${month}/${year}`;
    },

    formatDateTime(date) {
      if (!date) return "";

      const d = new Date(date);
      const dateStr = this.formatDate(date);
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const seconds = String(d.getSeconds()).padStart(2, "0");

      return `${dateStr} ${hours}:${minutes}:${seconds}`;
    },

    // Helper methods
    async OnEnter_legacy() {
      await this.search_name();
    },

    resetDialogs() {
      this.addpersonnel_temporarydialog = false;
      this.editpersonnel_temporarydialog = false;
      this.deletepersonnel_temporarydialog = false;
      this.csvpersonnel_temporarydialog = false;
      this.csvpersonnel_temporaryAssTeachdialog = false;
      this.csvpersonnel_temporaryOutofdialog = false;
    },

    resetForm(formRef) {
      if (this.$refs[formRef]) {
        this.$refs[formRef].reset();
        this.$refs[formRef].resetValidation();
      }
    },

    async exportPersonnelStatsToExcel() {
      if (!this.personnel_sCountsWithPercent.length) {
        this.showError("ไม่มีข้อมูลสำหรับส่งออก");
        return;
      }

      this.exportingExcel = true;
      try {
        const XLSX = await import("xlsx");

        const rows = this.personnel_sCountsWithPercent.map((item, idx) => ({
          ลำดับ: idx + 1,
          ประเภทตำแหน่ง: item.position_name || "ไม่ระบุ",
          "จำนวน (คน)": Number(item.count_grouptype) || 0,
          "สัดส่วน (%)": item.percent
        }));

        rows.push({
          ลำดับ: "",
          ประเภทตำแหน่ง: "รวมทั้งหมด",
          "จำนวน (คน)": this.totalPersonnelCount,
          "สัดส่วน (%)": 100
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        worksheet["!cols"] = [
          { wch: 8 },
          { wch: 45 },
          { wch: 14 },
          { wch: 12 }
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "สถิติตามประเภท");

        const today = this.formatDate(new Date());
        XLSX.writeFile(workbook, `สถิติบุคลากรตามประเภท_${today}.xlsx`);

        await this.writeSyslog(
          "export_excel",
          "personnel_temporary_stats",
          "export สถิติตามประเภท"
        );
      } catch (err) {
        console.error("Export Excel error:", err);
        this.showError("ไม่สามารถส่งออกไฟล์ได้");
      } finally {
        this.exportingExcel = false;
      }
    }
  }
};
</script>
<style scoped>
/* Modern Layout */
.personnel-management {
  background: #f5f7fa;
  min-height: 100vh;
}

.nav-wrapper {
  background: white;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 24px;
}

/* Header Card */
.header-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  background: #f0f4ff;
  padding: 12px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.3;
}

.header-subtitle {
  font-size: 14px;
  color: #666;
  margin: 4px 0 0 0;
}

/* Card Styles */
.card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  margin-bottom: 16px;
}

.card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

/* Chart Card */
.chart-card {
  height: 100%;
}

.chart-body {
  padding: 16px 24px 24px;
}

.chart-empty {
  text-align: center;
  padding: 48px 24px;
}

/* Stats Card */
.stats-card {
  height: 100%;
}

.count-btn {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

/* Info Card */
.info-card {
  text-align: center;
  padding: 40px 24px;
}

.info-icon-wrapper {
  background: #f0f4ff;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.info-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
}

.info-description {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

/* Action Panel */
.action-panel {
  padding: 16px;
}

.action-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  border-radius: 8px;
}

.action-btn .v-icon {
  font-size: 20px;
}

/* Search Card */
.search-card {
  padding: 24px;
}

.search-header {
  margin-bottom: 12px;
}

.search-hint {
  display: block;
  font-size: 12px;
  color: #888;
  margin-top: 4px;
}

.search-field {
  font-size: 14px;
}

.search-field-compact {
  max-width: 250px;
}

.filter-select .v-input__control {
  min-height: 40px !important;
}

/* Duplicate Card */
.duplicate-card {
  height: 100%;
}

/* Data Table Card */
.data-table-card {
  margin-bottom: 24px;
}

/* Clean Table */
.clean-table {
  font-size: 14px;
}

.clean-table thead {
  background: #fafafa;
}

.clean-table thead th {
  color: #666 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e0e0e0 !important;
}

.clean-table tbody tr {
  transition: background-color 0.2s;
}

.clean-table tbody tr:hover {
  background-color: #f8f9fa !important;
}

.clean-table tbody td {
  padding: 12px 16px !important;
  border-bottom: 1px solid #f0f0f0 !important;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 48px 24px;
  color: #999;
}

/* Responsive */
@media (max-width: 960px) {
  .main-container {
    padding: 16px;
  }

  .header-card {
    padding: 16px;
  }

  .header-title {
    font-size: 20px;
  }

  .card-header {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .search-field-compact {
    max-width: 100%;
  }

  .action-panel {
    padding: 12px;
  }

  .search-card {
    padding: 16px;
  }
}

/* Form Styles */
.v-text-field--outlined fieldset {
  border-color: #e0e0e0;
}

.v-text-field--outlined:not(.v-input--is-focused):not(.error--text) fieldset {
  border-color: #e0e0e0;
}

.v-text-field--outlined.v-input--is-focused fieldset {
  border-width: 1px;
}

/* Button Overrides */
.v-btn:not(.v-btn--text):not(.v-btn--outlined) {
  box-shadow: none !important;
}

.v-btn--outlined {
  border-width: 1px;
}

/* Chip Styles */
.v-chip {
  font-weight: 500;
  font-size: 13px;
}

/* Dialog Overrides */
.v-dialog > .v-card {
  border-radius: 12px;
}

.v-dialog .v-card__title {
  font-weight: 600;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

/* Divider */
.v-divider {
  border-color: #e0e0e0;
}

/* Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f5f7fa;
}

::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}
</style>
